import { useMemo } from "react";
import { getApiUrl } from "../../lib/api";
import { bookingLinks, marketplaceLink } from "./links";
import { BookingProblem } from "./parts";
import { RECORD_VIEWS, UNKEYED_VIEWS, isBookingView, type BookingProps, type ViewContext } from "./types";
import { ManageView } from "./views/ManageView";
import { CancelView } from "./views/CancelView";
import { RescheduleView } from "./views/RescheduleView";
import { ReviewView } from "./views/ReviewView";
import { BundleDatesView } from "./views/BundleDatesView";
import { BundleManageView } from "./views/BundleManageView";
import { FlexitimeConfirmView } from "./views/FlexitimeConfirmView";
import { FlexitimeRescheduleView } from "./views/FlexitimeRescheduleView";
import { SecurePaymentView } from "./views/SecurePaymentView";
import { SecurePayBookingView } from "./views/SecurePayBookingView";
import { RequestProposalView } from "./views/RequestProposalView";
import { PaymentSuccessView } from "./views/PaymentSuccessView";
import { ProductSuccessView } from "./views/ProductSuccessView";
import { OrderSuccessView } from "./views/OrderSuccessView";
import { GiftcardReceiptView } from "./views/GiftcardReceiptView";
import { WaitingListUnsubscribeView } from "./views/WaitingListUnsubscribeView";
import { ProductReviewView } from "./views/ProductReviewView";
import { SubmitReviewView } from "./views/SubmitReviewView";
import { ReconciliationView } from "./views/ReconciliationView";
import { BundleAuthView } from "./views/BundleAuthView";

// <baluu-booking>: the pages a customer reaches from their booking emails
// and their account, as one element (plans/websitev2-parity-m3-booking-management.md).
// The school's v2 site mounts it at v1's addresses (/bookings/…,
// /bundles/manage/…) with the ids from the address; each view is a port of
// v1's page and calls the same API.

const DEFAULT_MARKETPLACE_URL = "https://www.obby.co.uk";

export function Booking(props: BookingProps) {
  const ctx = useMemo<ViewContext>(() => {
    const marketplace = props.marketplaceUrl || DEFAULT_MARKETPLACE_URL;
    return {
      api: (props.apiUrl || getApiUrl()).replace(/\/+$/, ""),
      businessId: props.businessId ?? "",
      businessSlug: props.businessSlug ?? "",
      links: bookingLinks(props.widgetBaseUrl, props.linkQuery),
      primaryColor: props.primaryColor,
      toMarketplace: (path) => window.location.replace(marketplaceLink(marketplace, path)),
    };
  }, [props.apiUrl, props.businessId, props.businessSlug, props.widgetBaseUrl, props.linkQuery, props.marketplaceUrl, props.primaryColor]);

  const view = props.view;
  if (!isBookingView(view)) return <BookingProblem title="This page doesn’t exist" />;
  const bookingId = props.bookingId?.trim() ?? "";
  const bundleId = props.bundleId?.trim() ?? "";
  const recordId = props.recordId?.trim() ?? "";
  const keyed = (RECORD_VIEWS as readonly string[]).includes(view)
    ? recordId
    : view === "bundle-manage" || view === "bundle-auth"
      ? bundleId
      : bookingId;
  if (!keyed && !(UNKEYED_VIEWS as readonly string[]).includes(view)) return <BookingProblem title="This link isn’t complete" message="Please open it from your email again." />;

  switch (view) {
    case "manage":
      return <ManageView ctx={ctx} bookingId={bookingId} />;
    case "cancel":
      return <CancelView ctx={ctx} bookingId={bookingId} />;
    case "reschedule":
      return <RescheduleView ctx={ctx} bookingId={bookingId} />;
    case "review":
      return <ReviewView ctx={ctx} bookingId={bookingId} />;
    case "bundle-dates":
      return <BundleDatesView ctx={ctx} bookingId={bookingId} />;
    case "bundle-manage":
      return <BundleManageView ctx={ctx} bundleId={bundleId} />;
    case "flexitime-confirm":
      return <FlexitimeConfirmView ctx={ctx} bookingId={bookingId} verb={props.verb ?? ""} />;
    case "flexitime-reschedule":
      return <FlexitimeRescheduleView ctx={ctx} bookingId={bookingId} verb={props.verb ?? ""} date={props.date ?? ""} />;
    case "secure-payment":
      return <SecurePaymentView ctx={ctx} orderId={recordId} instalment={props.instalment?.trim() ?? ""} />;
    case "secure-pay-booking":
      return <SecurePayBookingView ctx={ctx} paymentId={recordId} />;
    case "request-proposal":
      return <RequestProposalView ctx={ctx} proposalId={recordId} />;
    case "payment-success":
      return <PaymentSuccessView ctx={ctx} bookingId={recordId} />;
    case "product-success":
      return <ProductSuccessView ctx={ctx} purchaseId={recordId} />;
    case "order-success":
      return <OrderSuccessView ctx={ctx} paymentIntentId={recordId} />;
    case "giftcard-receipt":
      return <GiftcardReceiptView ctx={ctx} giftCardId={recordId} />;
    case "waiting-list-unsubscribe":
      return <WaitingListUnsubscribeView ctx={ctx} entryId={recordId} />;
    case "product-review":
      return <ProductReviewView ctx={ctx} purchaseId={recordId} />;
    case "submit-review":
      return <SubmitReviewView ctx={ctx} />;
    case "reconciliation":
      return <ReconciliationView ctx={ctx} />;
    case "bundle-auth":
      return <BundleAuthView ctx={ctx} bundleId={bundleId} />;
  }
}
